import { RangeSetBuilder, StateEffect, StateField } from '@codemirror/state';
import { Decoration, DecorationSet, EditorView } from '@codemirror/view';

// Define a StateEffect to add decorations
const setDecorations = StateEffect.define<DecorationSet>();

// Define a StateField to hold the decoration set
export const decorationField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none; // Create an empty DecorationSet by default
  },
  update(decorations, transaction) {
    // Find all effects of "setDecorations" in the transaction
    if (Array.isArray(transaction?.effects)) {
      for (const effect of transaction.effects) {
        if (effect.is(setDecorations)) {
          // Apply the decorations from the effect
          return effect.value;
        }
      }
    }

    // If no relevant effect, retain the existing set with filtering
    return decorations.update({
      filter: () => true // Keep all existing decorations
    });
  },
  // Provide the field as an EditorView decoration source
  provide: (field) => EditorView.decorations.from(field)
});

export const errorMarker = (view: EditorView, start: number, end: number, error: string) => {
  const doc = view.state.doc;
  const docLength = doc.length;

  // Clamp offsets directly
  const fromPos = Math.max(0, Math.min(start, docLength));
  const toPos = Math.max(fromPos, Math.min(end, docLength));

  // Ensure at least 1 character is highlighted
  const finalTo = toPos <= fromPos ? fromPos + 1 : toPos;

  // Build decorations
  const builder = new RangeSetBuilder<Decoration>();

  // Line decoration (left border) - get line from the error position
  const line = doc.lineAt(fromPos);
  builder.add(line.from, line.from, Decoration.line({ class: 'jsonataErrorMargin' }));

  // Inline error marker
  builder.add(
    fromPos,
    Math.min(finalTo, docLength),
    Decoration.mark({
      class: 'jsonataErrorMarker',
      attributes: {
        'aria-label': error || 'Unknown error',
        title: error || 'Unknown error'
      }
    })
  );

  const decorations = builder.finish();
  view.dispatch({
    effects: setDecorations.of(decorations)
  });
};

export const clearMarkers = (view: EditorView) => {
  // Dispatch a `setDecorations` effect with an empty DecorationSet
  view.dispatch({
    effects: setDecorations.of(Decoration.none) // Clear all decorations
  });
};
